import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import { Swiper, SwiperSlide } from "swiper/react";
import useInputKeys from "hooks/useInputKeys";
import { useGuessNumber } from "hooks/game";
import "swiper/css";
import "./index.scss";

function PlayGame({
  setHasWon,
  history,
  setTries,
  setGoldenNumber,
  setCurrentScore,
}) {
  const { strInput, setStrInput, handleClearStr } = useInputKeys();
  const swiperRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [guesses, setGuesses] = useState(history?.guesses || []);
  const [guessState, setGuessState] = useState(null);
  const [gameState, setGameState] = useState(false);
  const [wink, setWink] = useState(true);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [arrowStyle, setArrowStyle] = useState({});

  const hasSubmittedRef = useRef(false);

  const { mutate: guessNumber, isLoading } = useGuessNumber();

  const setSwiper = (swiper) => {
    swiperRef.current = swiper;
  };

  useEffect(() => {
    setTimeout(() => {
      swiperRef?.current?.slideTo(activeSlide);
    }, 100);
  }, [activeSlide]);

  useEffect(() => {
    setActiveSlide(guesses.length);
  }, [guesses]);

  useEffect(() => {
    const timer = setTimeout(() => setWink((prev) => !prev), 500);
    return () => clearTimeout(timer);
  }, [wink]);

  useEffect(() => {
    const initialGuesses = history?.guesses || [];
    const pots = initialGuesses.map((g) => g.pot);

    if (pots.length === 0 && history?.currentPot) {
      setScoreHistory([history.currentPot]);
    } else {
      setScoreHistory(pots);
    }

    setGuesses(initialGuesses);
  }, [history]);

  const getValue = (i) => {
    if (i < strInput.length) return strInput[i];
    if (i === strInput.length) return wink ? "-" : "";
    return "-";
  };

  const handleEdit = (i) => {
    if (guesses[guesses.length - 1]?.hint !== "you won") {
      setStrInput((prev) => prev.slice(0, i));
    }
  };

  const handleGuessAPI = (guess) => {
    guessNumber(guess, {
      onSuccess: (data) => {
        if (data?.status === "OK") {
          const hint = data.message.toLowerCase();
          setGuessState(hint);

          setScoreHistory((prev) => {
            const last = prev[prev.length - 1];
            const next = Math.round((last / 2) * 100) / 100;
            return [...prev, next];
          });

          setTimeout(() => {
            setGuesses((prev) => [...prev, { guessedNumber: guess, hint }]);
            if (hint !== "you won") {
              setGuessState(null);
              setStrInput("");
              setCurrentScore(data.pot);
              swiperRef.current.slideNext(1);
            } else {
              setGameState(true);
              setHasWon(true);
              setGoldenNumber(guess);
            }
          }, 2000);
        }
      },
      onError: (e) => {
        console.error("Guess failed:", e);
      },
    });
  };

  const handleGuess = () => {
    const guess = Number(strInput);
    if (!isNaN(guess) && strInput.length === 6) {
      handleGuessAPI(guess);
    }
  };

  useEffect(() => {
    const lastChar = strInput.slice(-1);
    const notNumber = isNaN(lastChar);

    if (guessState === "you won") return;

    if (notNumber || strInput.length > 6) {
      handleClearStr();
      return;
    }

    if (strInput.length === 6 && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      handleGuess();
    }

    if (strInput.length < 6) {
      hasSubmittedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strInput]);

  const getScore = (index) => {
    if (!scoreHistory.length) return "-";

    if (scoreHistory[index] !== undefined) {
      return Math.round(scoreHistory[index] * 100) / 100;
    }

    let score = scoreHistory[scoreHistory.length - 1];
    for (let i = scoreHistory.length; i <= index; i++) {
      score = Math.round((score / 2) * 100) / 100;
    }
    return score;
  };

  const getGuessesSlides = () => {
    if (guesses.length === 0) return ["------", "------", "------"];
    if (guesses.length === 1) return [...guesses, "------", "------"];
    return [...guesses, "------"];
  };

  const getArrowStyle = (direction) => {
    let rotation = "0deg";
    let color = "#00FF00";

    if (direction === "up") {
      rotation = "90deg";
      color = "#00FF00";
    } else if (direction === "down") {
      rotation = "-90deg";
      color = "#FF0000";
    } else if (direction === "you won") {
      rotation = "0deg";
      color = "#00FF00";
    }

    return {
      transform: `rotate(${rotation})`,
      fill: color,
    };
  };

  useEffect(() => {
    setTries(guesses.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guesses]);

  useEffect(() => {
    setStrInput("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setArrowStyle(getArrowStyle(guessState));
    }
  }, [guessState, isLoading]);

  return (
    <Box className="play-guess-number-game-component">
      <Box className="guesses-box">
        <Swiper
          slidesPerView={3}
          spaceBetween={1}
          className="guessesSwiper"
          onSwiper={setSwiper}
          centeredSlides={true}
        >
          {getGuessesSlides().map((item, index) => {
            const guessObj = guesses[index];
            return (
              <SwiperSlide
                key={`slide-${index}`}
                className={`guess-item ${
                  activeSlide === index ? "active-guess" : ""
                }`}
              >
                <Box className="score">{getScore(index)}</Box>
                <Box
                  className={`guess ${
                    gameState && guessObj?.hint === "you won" ? "active" : ""
                  }`}
                >
                  {guessObj && (
                    <>
                      ?
                      <span className="comparison-indicator">
                        {guessObj.hint.toLowerCase() === "up"
                          ? ">"
                          : guessObj.hint.toLowerCase() === "down"
                          ? "<"
                          : "="}
                      </span>
                    </>
                  )}
                  {guessObj
                    ? String(guessObj.guessedNumber).padStart(6, "0")
                    : item}
                </Box>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Box>

      <Box className="input-guess-box">
        <Box className="inputs">
          {Array.from({ length: 6 }).map((_, index) => (
            <InputBase
              key={index}
              fullWidth
              readOnly
              className={`digit-input ${
                guessState === "you won" ? "equal" : guessState || ""
              }`}
              sx={{ fontFamily: "bahnschrift" }}
              value={getValue(index)}
              onClick={() => handleEdit(index)}
            />
          ))}
        </Box>
        <Box className={`arrow-box ${guessState || ""}`}>
          <svg
            width="31"
            height="34"
            viewBox="0 0 31 34"
            xmlns="http://www.w3.org/2000/svg"
            className={isLoading ? "spinning" : ""}
            onAnimationEnd={() => {
              setArrowStyle(getArrowStyle(guessState));
            }}
            style={arrowStyle}
          >
            <path d="M2 20.4641C-0.666664 18.9245 -0.666667 15.0755 2 13.5359L24.5 0.545517C27.1667 -0.994083 30.5 0.930417 30.5 4.00962L30.5 29.9904C30.5 33.0696 27.1667 34.9941 24.5 33.4545L2 20.4641Z" />
          </svg>
        </Box>
      </Box>
    </Box>
  );
}

export default PlayGame;
